import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UserProfilePatchInput } from '@today/models'
import type { UiLanguage, UserProfileDto } from '@today/types'
import { useState } from 'react'
import { apiMessage } from '@/hooks/offers/api-message'
import { changeLanguage, resetLanguage } from '@/i18n'
import { userProfileService } from '@/services/user-profile.service'

/** The caller's profile cache key (TO-197). */
export const USER_PROFILE_QUERY_KEY = ['user-profile', 'me'] as const

/** The signed-in user's own profile. */
export const useUserProfileQuery = () =>
	useQuery({ queryKey: USER_PROFILE_QUERY_KEY, queryFn: () => userProfileService.get() })

/** Apply a saved language: a chosen one switches the UI, `null` returns to automatic (TO-197 REQ-010). */
export const applyLanguage = (language: UiLanguage | null): Promise<void> =>
	language === null ? resetLanguage() : changeLanguage(language)

/** `PATCH` the profile; the cache takes the server's answer, a changed language is applied after success. */
export function useProfilePatch() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: UserProfilePatchInput) => userProfileService.patch(body),
		onSuccess: async (profile, body) => {
			queryClient.setQueryData(USER_PROFILE_QUERY_KEY, profile)
			if (body.language !== undefined) await applyLanguage(profile.language)
		},
	})
}

/** The trimmed name when it has 1–128 code points (as the API counts them), else undefined. */
export const parseDisplayName = (text: string): string | undefined => {
	const name = text.trim()
	const length = [...name].length
	return length >= 1 && length <= 128 ? name : undefined
}

/** A select value ('' = automatic / device) as the API field. */
const nullable = <T extends string>(value: string): T | null => (value === '' ? null : (value as T))

/** The fields of a profile draft that differ from the saved profile. */
const profileChanges = (
	profile: UserProfileDto,
	draft: { name: string; language: string; timezone: string },
): UserProfilePatchInput => {
	const language = nullable<UiLanguage>(draft.language)
	const timezone = nullable<string>(draft.timezone)
	return {
		...(draft.name !== profile.displayName && { displayName: draft.name }),
		...(language !== profile.language && { language }),
		...(timezone !== profile.timezone && { timezone }),
	}
}

/** Edits of the Profile section, over the saved profile: drafts, refusals, save and reset. */
export function useProfileForm(profile: UserProfileDto | undefined) {
	const [name, setName] = useState<string | null>(null)
	const [language, setLanguage] = useState<string | null>(null)
	const [timezone, setTimezone] = useState<string | null>(null)
	const [nameError, setNameError] = useState<string | null>(null)
	const patch = useProfilePatch()
	const draft = {
		name: name ?? profile?.displayName ?? '',
		language: language ?? profile?.language ?? '',
		timezone: timezone ?? profile?.timezone ?? '',
	}
	const clearDrafts = () => {
		setName(null)
		setLanguage(null)
		setTimezone(null)
	}
	return {
		draft,
		nameError,
		error: patch.error ? (apiMessage(patch.error) ?? null) : null,
		hasFailed: patch.isError,
		isSaving: patch.isPending,
		saved: patch.isSuccess,
		setName: (value: string) => {
			setName(value)
			setNameError(null)
		},
		setLanguage,
		setTimezone,
		save: () => {
			if (!profile) return
			const valid = parseDisplayName(draft.name)
			if (valid === undefined) return setNameError('invalid')
			const body = profileChanges(profile, { ...draft, name: valid })
			if (Object.keys(body).length > 0) patch.mutate(body, { onSuccess: clearDrafts })
		},
		reset: () => patch.mutate({ displayName: null }, { onSuccess: () => setName(null) }),
	}
}
